// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { SystemUser } from '@Constants/organisation';
import { findUser, useLocalMessage } from '@Helpers/';
import IconLabel from '@Atoms/Info/IconLabel/IconLabel';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { classList } from '@Helpers/className';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';

import './AssigneeInfo.scss';
import { ICONS } from '@Constants/iconsApp';

interface AssigneeInfoProps {
    assignee?: string | null;
    avatarOnly?: boolean;
    placement?: TooltipPlacement;
    noAvatar?: boolean;
    onClick?: (assignee?: string) => void;
}

const AssigneeInfo = ({
    assignee = '',
    avatarOnly = false,
    noAvatar = false,
    onClick,
}: AssigneeInfoProps): ReactElement => {
    const i = useLocalMessage();

    const { data: userList = [] } = useListQuery({
        controller: client.OrganisationController,
        queryParams: {
            selector: { head: [{ name: 'listVisibleUsers' }], name: 'get-visible-users' },
        },
        manual: true,
        fetchOnStart: false,
    });

    const assigneeUser = assignee !== SystemUser.login ? findUser(assignee, userList) : SystemUser;

    const { name = '', organisation = '', avatar = '' } = assigneeUser || {};

    return assignee ? (
        <div className={classList('assignee-info', { clickable: !!onClick })} onClick={() => onClick?.(assignee)}>
            {avatarOnly ? (
                <UserAvatar
                    name={name || assignee}
                    avatar={avatar}
                    tooltipCustom={!!assigneeUser ? `${organisation}/${name}` : assignee}
                    showTooltip
                />
            ) : (
                <IconLabel
                    icon={!noAvatar && <UserAvatar name={name || assignee} avatar={avatar} />}
                    label={name || assignee}
                />
            )}
        </div>
    ) : (
        <div className={classList('assignee-info__unassigned', { clickable: !!onClick })} onClick={() => onClick?.()}>
            <THTooltip title={i('general.unassigned')}>
                <ICONS.QuestionCircleOutlined />
            </THTooltip>
        </div>
    );
};

export default AssigneeInfo;
